import * as Api from '@ViewModels';
import { toPng } from '@mickykebe-lev/html-to-image';
import { css } from 'aphrodite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ILocationState } from '../../../models';
import {
	accountHasComplianceEnabled,
	createContentStateWithHtmlStringValue,
	getRatingIntFromText,
} from '../../../models/UiUtils';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useAccountLoad, useAttachmentQuery } from '../../../queries';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { useCreateSocialPostMutation } from '../../../queries/Social/useCreateSocialPostMutation';
import { ImpersonationContextViewModel } from '../../../viewmodels/AdminViewModels';
import { lastUsedComplianceRecipientEmail } from '../campaigns/CampaignDetailsSummary';
import { TestimonialControls } from './TestimonialControls';
import { TestimonialPreview } from './TestimonialPreview';
import { DEFAULT_COLORS, DEFAULT_POST_DESCRIPTIONS, IMAGE_DIMENSIONS, MAX_CONTENT_LENGTH } from './constants';
import { useGetImageUrl } from './hooks/useGetImageUrl';
import { styleSheet } from './styles';
import { LocationState, StoredPreferences, TestimonialImageEditorProps } from './types';

const getImpersonationContextPlain = (
	ctx: ImpersonationContextViewModel | Api.IImpersonationContext | undefined
): Api.IImpersonationContext | undefined =>
	ctx && 'toJs' in ctx && typeof (ctx as ImpersonationContextViewModel).toJs === 'function'
		? (ctx as ImpersonationContextViewModel).toJs()
		: (ctx as Api.IImpersonationContext | undefined);

const isImpersonationValid = (ctx: ImpersonationContextViewModel | Api.IImpersonationContext | undefined): boolean =>
	!!(ctx && 'isValid' in ctx && (ctx as ImpersonationContextViewModel).isValid);

const STORAGE_KEY = 'testimonialEditorPreferences';

const getStoredPreferences = (): StoredPreferences | null => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : null;
	} catch (error) {
		// Silently handle localStorage errors and return null for defaults
		// In production, you might want to log this, but in tests it's expected
		if (process.env.NODE_ENV !== 'test') {
			console.error('Error reading preferences from localStorage:', error);
		}
		return null;
	}
};

const savePreferences = (preferences: StoredPreferences): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
	} catch (error) {
		// Silently handle localStorage errors in tests
		if (process.env.NODE_ENV !== 'test') {
			console.error('Error saving preferences to localStorage:', error);
		}
	}
};

const getRandomPostDescription = (): string => {
	return DEFAULT_POST_DESCRIPTIONS[Math.floor(Math.random() * DEFAULT_POST_DESCRIPTIONS.length)];
};

const generateFileName = (sourceType: 'google_review' | 'survey_response', authorName: string): string => {
	const sourcePrefix = sourceType === 'google_review' ? 'google-review' : 'survey-response';
	const authorSlug = authorName ? authorName.replace(/\s+/g, '-').toLowerCase() : 'testimonial';
	return `${sourcePrefix}-${authorSlug}-${Date.now()}.png`;
};

export const TestimonialImageEditor: React.FC<TestimonialImageEditorProps> = ({
	testimonial,
	containerStyles,
	builderStyles,
	impersonationContext,
	accountId,
	onPostCreated,
}: TestimonialImageEditorProps) => {
	const userSession = useUserSession();
	const impersonationContextPlain = getImpersonationContextPlain(impersonationContext);
	const account = impersonationContextPlain?.account ?? userSession.account;

	const { data: accountData } = useAccountLoad({
		accountId: account?.id ?? '',
		meta: { impersonationContext },
	});
	const branding = accountData?.preferences?.googleReviewPostBranding;

	const { data: imageData } = useAttachmentQuery({
		id: branding?.image?.id ?? '',
		enabled: !!branding?.image?.id,
		impersonationContext,
	});

	// Get stored preferences (used when not in admin/impersonation context)
	const storedPrefs = getStoredPreferences();
	const { getImageUrl } = useGetImageUrl(impersonationContext);

	// State - use account preferences as defaults when available, else localStorage
	const [bgColor, setBgColor] = useState<string>(
		branding?.backgroundColor ?? storedPrefs?.bgColor ?? DEFAULT_COLORS.background
	);
	const defaultImageAttachment = branding?.image ? (imageData ?? branding.image) : undefined;
	const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<{
		slug: string;
		fileAttachment: Api.IFileAttachment;
	} | null>(defaultImageAttachment ? { slug: 'account-default', fileAttachment: defaultImageAttachment } : null);

	const [textColor, setTextColor] = useState<string>(
		branding?.foregroundColor ?? storedPrefs?.textColor ?? DEFAULT_COLORS.text
	);
	const [reviewText, setReviewText] = useState(
		(testimonial.type === 'google_review' ? testimonial.data.text : testimonial.data.feedback) || ''
	);
	const [authorName, setAuthorName] = useState(
		testimonial.type === 'google_review'
			? testimonial.data.author || ''
			: testimonial.data.contact
				? `${testimonial.data.contact.firstName || ''} ${testimonial.data.contact.lastName || ''}`.trim()
				: 'Anonymous'
	);
	const [rating, _setRating] = useState(
		testimonial.type === 'google_review' ? getRatingIntFromText(testimonial.data.rating) : testimonial.data.rating || 0
	);
	const [includeGoogleLogo, setIncludeGoogleLogo] = useState(
		testimonial.type === 'google_review'
			? (branding?.includeGoogleLogo ?? storedPrefs?.includeGoogleLogo ?? true)
			: false
	);
	const [includeAuthor, setIncludeAuthor] = useState(storedPrefs?.includeAuthor ?? true);
	const [includeStars, setIncludeStars] = useState(branding?.includeStars ?? storedPrefs?.includeStars ?? true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [sourceType] = useState<'google_review' | 'survey_response'>(testimonial.type);
	const [isCreatingDraft, setIsCreatingDraft] = useState(false);
	const [sendFromUser, setSendFromUser] = useState<Api.UserViewModel | null>(null);

	// Sync state when account branding first loads (accountData is async)
	const hasAppliedBrandingRef = useRef(false);
	useEffect(() => {
		if (branding && !hasAppliedBrandingRef.current) {
			hasAppliedBrandingRef.current = true;
			setBgColor(branding.backgroundColor ?? DEFAULT_COLORS.background);
			setSelectedBackgroundImage(
				branding.image ? { slug: 'account-default', fileAttachment: imageData ?? branding.image } : null
			);
			setTextColor(branding.foregroundColor ?? DEFAULT_COLORS.text);
			if (testimonial.type === 'google_review') {
				setIncludeGoogleLogo(branding.includeGoogleLogo ?? true);
			}
			setIncludeStars(branding.includeStars ?? true);
		}
	}, [branding, testimonial.type, imageData]);
	useEffect(() => {
		hasAppliedBrandingRef.current = false;
	}, [account?.id]);

	// Update selectedBackgroundImage when imageData loads (provides url for account default image)
	useEffect(() => {
		if (
			branding?.image &&
			imageData &&
			selectedBackgroundImage?.slug === 'account-default' &&
			selectedBackgroundImage.fileAttachment.id === branding.image.id &&
			!selectedBackgroundImage.fileAttachment.url
		) {
			setSelectedBackgroundImage({ slug: 'account-default', fileAttachment: imageData });
		}
	}, [branding?.image, imageData, selectedBackgroundImage]);

	// Save preferences whenever they change
	useEffect(() => {
		savePreferences({
			bgColor,
			textColor,
			includeGoogleLogo,
			includeAuthor,
			includeStars,
		});
	}, [bgColor, textColor, includeGoogleLogo, includeAuthor, includeStars]);

	// Hooks
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const previewRef = useRef<HTMLDivElement>(null);
	const imageGenerationRef = useRef<HTMLDivElement>(null);

	// Derived state
	const isContentTooLong = reviewText.length > MAX_CONTENT_LENGTH;
	const requiresSendFrom = isImpersonationValid(impersonationContext) && !sendFromUser;

	// Mutations
	const { mutateAsync: uploadImage } = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			setErrorMessage('Error uploading image. Please try again or continue without an image.');
		},
	});

	const accountIdForNav = accountId ?? impersonationContextPlain?.account?.id;

	const initialBackgroundImage = useMemo(
		() => (branding?.image ? { slug: 'account-default' as const, fileAttachment: imageData ?? branding.image } : null),
		[branding?.image, imageData]
	);

	const { mutateAsync: createSocialPost } = useCreateSocialPostMutation({
		onSuccess: async response => {
			onPostCreated?.();

			const postVm = new Api.SocialMediaPostViewModel(userSession, response);
			await postVm.load();

			if (isImpersonationValid(impersonationContext) && accountIdForNav) {
				const locationState: ILocationState<
					Api.SocialMediaPostViewModel,
					{ impersonationContext: Api.IImpersonationContext }
				> = {
					viewModel: postVm,
					model: { impersonationContext: impersonationContextPlain },
				};
				fullscreenModal.history.push({
					pathname: `/accounts/${accountIdForNav}/content-calendar/post/create`,
					state: locationState,
				});
			} else {
				const locationState: LocationState = {
					viewModel: postVm,
				};
				fullscreenModal.history.push({
					pathname: `/social-media/post/edit-post-draft/${response.id}`,
					state: locationState,
				});
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			setIsCreatingDraft(false);
		},
	});

	// Handlers
	const generateImage = async (): Promise<void> => {
		if (!imageGenerationRef.current) return;

		if (isContentTooLong) {
			toast.error(`Content is too long. Please reduce to ${MAX_CONTENT_LENGTH} characters or less.`);
			return;
		}

		if (requiresSendFrom) {
			toast.error('Please select a user to post from before creating.');
			return;
		}

		setErrorMessage(null);

		const toastId = toast.loading('Generating image...');

		try {
			const backgroundImageUrl = selectedBackgroundImage
				? await getImageUrl(selectedBackgroundImage?.fileAttachment)
				: null;

			const dataUrl = await toPng(imageGenerationRef.current, {
				cacheBust: true,
				pixelRatio: 3,
				width: IMAGE_DIMENSIONS.width,
				height: IMAGE_DIMENSIONS.height,
				style: {
					width: `${IMAGE_DIMENSIONS.width}px`,
					height: `${IMAGE_DIMENSIONS.height}px`,
					borderRadius: '0px',
					boxShadow: 'none',
					wordWrap: 'break-word',
					overflowWrap: 'break-word',
					hyphens: 'auto',
					backgroundColor: selectedBackgroundImage ? 'transparent' : bgColor,
					backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
					backgroundSize: selectedBackgroundImage ? 'cover' : 'contain',
					backgroundPosition: 'top left',
					color: textColor,
					fontFamily: 'Sora',
				},
				filter: () => true,
				backgroundColor: selectedBackgroundImage ? 'transparent' : bgColor,
			});

			const response = await fetch(dataUrl);
			const blob = await response.blob();
			const file = new File([blob], generateFileName(sourceType, authorName), { type: 'image/png' });

			const fileAttachment = await uploadImage({
				files: [file],
				resizeForSocialMedia: true,
				impersonationContext: impersonationContextPlain,
			});

			toast.loading('Creating draft post...', { id: toastId });
			await createDraft(dataUrl, fileAttachment[0], toastId);
		} catch (error) {
			toast.error('Failed to generate image. Please try again.', { id: toastId });
			errorMessages.pushApiError(error);
			setErrorMessage('Unable to generate image. Please try again or continue without an image.');
		}
	};

	const createDraft = async (
		imageUrl: string | null,
		fileAttachment?: any,
		toastId?: string | number
	): Promise<void> => {
		setIsCreatingDraft(true);

		const content = createContentStateWithHtmlStringValue(getRandomPostDescription()).getRawRichTextContent();

		const isComplianceEnabled = accountHasComplianceEnabled(
			userSession,
			impersonationContext as ImpersonationContextViewModel
		);
		const sendWithComplianceEmail = localStorage.getItem(lastUsedComplianceRecipientEmail) ?? undefined;

		const createRequest: Api.ICreateSocialMediaPostRequest = {
			name: `Testimonial from ${authorName || 'Customer'}`,
			content,
			attachments: fileAttachment ? [fileAttachment] : [],
			overlayLogo: false,
			designatedTargets: sendFromUser
				? sendFromUser.toJs().socialMediaConnectedAccounts?.map((socialConnection: Api.ISocialMediaConnection) => {
						return {
							provider: socialConnection.type as Api.SocialMediaType,
							pageId: socialConnection.postTargetId,
							userId: socialConnection.userId,
						};
					})
				: [],
			googleReviewId: testimonial.type === 'google_review' ? testimonial.data.id : undefined,
			surveyResponseId: testimonial.type === 'survey_response' ? testimonial.data.id : undefined,
			sendWithCompliance: isComplianceEnabled ? true : undefined,
			sendWithComplianceEmail: isComplianceEnabled ? sendWithComplianceEmail : undefined,
		};

		try {
			await createSocialPost({
				request: createRequest,
				forUserId: sendFromUser?.id ?? userSession.user?.id,
				impersonationContext: { ...impersonationContextPlain, user: { id: sendFromUser?.id ?? userSession.user?.id } },
			});
			if (toastId) {
				toast.success('Post draft created successfully!', { id: toastId });
			}
			setIsCreatingDraft(false);
		} catch (error) {
			if (toastId) {
				toast.error('Failed to create draft post. Please try again.', { id: toastId });
			}
			setIsCreatingDraft(false);
			errorMessages.pushApiError(error);
		}
	};

	return (
		<div className={css(styleSheet.editorContainer, ...(containerStyles ?? []))}>
			<TestimonialControls
				builderStyles={builderStyles}
				bgColor={bgColor}
				textColor={textColor}
				reviewText={reviewText}
				authorName={authorName}
				includeGoogleLogo={includeGoogleLogo}
				includeAuthor={includeAuthor}
				includeStars={includeStars}
				sourceType={sourceType}
				isContentTooLong={isContentTooLong}
				createPostDisabled={requiresSendFrom}
				onBgColorChange={setBgColor}
				onTextColorChange={setTextColor}
				onReviewTextChange={setReviewText}
				onAuthorNameChange={setAuthorName}
				onIncludeGoogleLogoChange={() => setIncludeGoogleLogo(!includeGoogleLogo)}
				onIncludeAuthorChange={() => setIncludeAuthor(!includeAuthor)}
				onIncludeStarsChange={() => setIncludeStars(!includeStars)}
				onGenerateImage={generateImage}
				onSendFromChange={setSendFromUser}
				onBackgroundImageChange={setSelectedBackgroundImage}
				initialBackgroundImage={initialBackgroundImage}
			/>
			<TestimonialPreview
				bgColor={bgColor}
				textColor={textColor}
				reviewText={reviewText}
				authorName={authorName}
				rating={rating}
				includeGoogleLogo={includeGoogleLogo}
				includeAuthor={includeAuthor}
				includeStars={includeStars}
				sourceType={sourceType}
				isCreatingDraft={isCreatingDraft}
				errorMessage={errorMessage}
				previewRef={previewRef}
				imageGenerationRef={imageGenerationRef}
				selectedBackgroundImage={selectedBackgroundImage}
				onCreateDraft={createDraft}
			/>
		</div>
	);
};
